import { useState, useRef, useEffect } from "react";
import { coloringImages, drawingColors } from "./GameData";

// Free Drawing Canvas Component
export function DrawingCanvas({ t, onBack }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState("#000000");
  const [brushSize, setBrushSize] = useState(5);
  const [isEraser, setIsEraser] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.strokeStyle = isEraser ? "#FFFFFF" : color;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveDrawing = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = "mio-disegno.png";
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-400 to-indigo-400 p-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          data-testid="drawing-back-btn"
        >
          ← {t.backHome}
        </button>
        
        <h2 className="text-3xl font-bold text-white text-center mb-4" data-testid="drawing-title">🎨 {t.drawFree}</h2>
        
        <div className="bg-white rounded-3xl p-4 shadow-xl">
          {/* Canvas */}
          <canvas
            ref={canvasRef}
            width={800}
            height={500}
            className="w-full border-4 border-gray-200 rounded-2xl cursor-crosshair touch-none"
            data-testid="drawing-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          
          {/* Tools */}
          <div className="mt-4 flex flex-wrap gap-4 items-center justify-center">
            {/* Colors */}
            <div className="flex gap-1 flex-wrap justify-center">
              {drawingColors.map((c) => (
                <button
                  key={c}
                  onClick={() => { setColor(c); setIsEraser(false); }}
                  className={`w-8 h-8 rounded-full border-2 ${color === c && !isEraser ? "border-gray-800 scale-125" : "border-gray-300"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`drawing-color-${c.replace("#", "")}`}
                />
              ))}
            </div>
            
            {/* Brush Size */}
            <div className="flex items-center gap-2">
              <span className="text-gray-600">{t.brushSize}:</span>
              <input
                type="range"
                min="1"
                max="30"
                value={brushSize}
                onChange={(e) => setBrushSize(parseInt(e.target.value))}
                className="w-24"
                data-testid="brush-size-slider"
              />
              <span className="text-gray-600" data-testid="brush-size-value">{brushSize}px</span>
            </div>
            
            {/* Eraser */}
            <button
              onClick={() => setIsEraser(!isEraser)}
              className={`px-4 py-2 rounded-xl font-bold ${isEraser ? "bg-pink-500 text-white" : "bg-gray-200 text-gray-700"}`}
              data-testid="eraser-btn"
            >
              🧽 {t.eraser}
            </button>
            
            {/* Clear */}
            <button
              onClick={clearCanvas}
              className="px-4 py-2 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600"
              data-testid="clear-canvas-btn"
            >
              🗑️ {t.clear}
            </button>
            
            {/* Save */}
            <button
              onClick={saveDrawing}
              className="px-4 py-2 rounded-xl bg-green-500 text-white font-bold hover:bg-green-600"
              data-testid="save-drawing-btn"
            >
              💾 {t.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Coloring Book Component (brush painting)
export function ColoringBook({ t, lang, onBack }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentColor, setCurrentColor] = useState("#FF0000");
  const [brushSize, setBrushSize] = useState(12);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    if (selectedImage) clearCanvas();
  }, [selectedImage]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    return {
      x: (cx - rect.left) * (canvas.width / rect.width),
      y: (cy - rect.top) * (canvas.height / rect.height),
    };
  };

  const startPaint = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    drawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
    // dot on tap
    ctx.fillStyle = currentColor;
    ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const paint = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = currentColor;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopPaint = () => { drawingRef.current = false; };

  const saveColoring = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Compose paint canvas + outline into an export canvas
    const exportC = document.createElement("canvas");
    exportC.width = canvas.width; exportC.height = canvas.height;
    const ex = exportC.getContext("2d");
    ex.drawImage(canvas, 0, 0);
    const svg = document.querySelector('[data-testid="coloring-svg"]');
    if (svg) {
      const data = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      img.onload = () => {
        ex.drawImage(img, 0, 0, exportC.width, exportC.height);
        const link = document.createElement("a");
        link.download = "mio-disegno.png";
        link.href = exportC.toDataURL();
        link.click();
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(data)));
    }
  };

  if (!selectedImage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-orange-400 to-red-400 p-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={onBack}
            className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
            data-testid="coloring-back-btn"
          >
            ← {t.backHome}
          </button>
          
          <h2 className="text-3xl font-bold text-white text-center mb-6">🖍️ {t.coloringBook}</h2>
          <p className="text-white text-center mb-6">{t.chooseImage}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {coloringImages.map((img) => (
              <button
                key={img.id}
                onClick={() => setSelectedImage(img)}
                className="bg-white rounded-2xl p-6 shadow-lg hover:scale-105 transition-transform"
                data-testid={`coloring-image-${img.id}`}
              >
                <div className="text-5xl mb-2">{img.icon}</div>
                <div className="font-bold text-gray-700">{lang === "it" ? img.name_it : img.name_en}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-orange-400 to-red-400 p-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => setSelectedImage(null)}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
          data-testid="coloring-choose-image-btn"
        >
          ← {t.chooseImage}
        </button>
        
        <h2 className="text-3xl font-bold text-white text-center mb-2">
          🖍️ {lang === "it" ? selectedImage.name_it : selectedImage.name_en}
        </h2>
        <p className="text-white/90 text-center mb-4 text-sm">
          {lang === "it" ? "Scegli un colore e dipingi con il dito o il mouse!" : "Pick a color and paint with your finger or mouse!"}
        </p>
        
        <div className="bg-white rounded-3xl p-6 shadow-xl">
          {/* Painting area: canvas (paint) + SVG outline overlay */}
          <div className="flex justify-center mb-4">
            <div className="relative w-80 h-80 border-4 border-gray-200 rounded-2xl overflow-hidden" style={{ touchAction: "none" }}>
              <canvas
                ref={canvasRef}
                width={320}
                height={320}
                className="absolute inset-0 w-full h-full cursor-crosshair"
                data-testid="coloring-canvas"
                onMouseDown={startPaint}
                onMouseMove={paint}
                onMouseUp={stopPaint}
                onMouseLeave={stopPaint}
                onTouchStart={startPaint}
                onTouchMove={paint}
                onTouchEnd={stopPaint}
              />
              <svg
                viewBox="0 0 100 100"
                className="absolute inset-0 w-full h-full pointer-events-none"
                data-testid="coloring-svg"
              >
                {selectedImage.paths.map((path, idx) => (
                  <path
                    key={idx}
                    d={path.d}
                    fill="none"
                    stroke={path.stroke || "#333"}
                    strokeWidth={(path.strokeWidth || 2) + 0.5}
                    data-testid={`coloring-path-${idx}`}
                  />
                ))}
              </svg>
            </div>
          </div>
          
          {/* Color Palette */}
          <div className="flex flex-wrap gap-2 justify-center mb-4">
            {drawingColors.map((c) => (
              <button
                key={c}
                onClick={() => setCurrentColor(c)}
                className={`w-10 h-10 rounded-full border-2 transition-transform ${currentColor === c ? "border-gray-800 scale-125" : "border-gray-300"}`}
                style={{ backgroundColor: c }}
                data-testid={`coloring-color-${c.replace("#", "")}`}
              />
            ))}
          </div>

          {/* Brush size */}
          <div className="flex items-center gap-2 justify-center mb-4">
            <span className="text-gray-600">🖌️ {t.brushSize || "Pennello"}:</span>
            <input
              type="range" min="4" max="40" value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
              className="w-32" data-testid="coloring-brush-slider"
            />
            <span className="text-gray-600" data-testid="coloring-brush-value">{brushSize}px</span>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={clearCanvas}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600"
              data-testid="coloring-clear-btn"
            >
              🔄 {t.clear}
            </button>
            <button
              onClick={saveColoring}
              className="flex-1 py-3 rounded-xl bg-green-500 text-white font-bold hover:bg-green-600"
              data-testid="coloring-save-btn"
            >
              💾 {t.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default { DrawingCanvas, ColoringBook };
